import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createTestSeriesAPI, fetchCoursesAPI } from "../../utils/api";
import { showSuccess } from "../../utils/sweetAlert";
import { ErrorAlert } from "../../components/ui";
import TestSeriesForm, {
  EMPTY_EXTRAS,
  extrasPayload,
  fieldErrorsFrom,
  timingPayload,
  validateExtras,
  validateTiming,
} from "./TestSeriesForm";
import "./CreateTestSeries.css";

function CreateTestSeries() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    course: "",
    type: "free",
    visibility: "draft",
    section_toggle: false,
    nested_sections: false,
    sectional_timing: true,
    duration_minutes: "",
    ...EMPTY_EXTRAS,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState("");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setCoursesLoading(true);
        setCoursesError("");
        const response = await fetchCoursesAPI();

        if (response?.success && Array.isArray(response.data)) {
          setCourses(response.data);
          // Preselect the first course (the select's values are course names)
          if (response.data.length > 0) {
            setFormData((prev) => (prev.course ? prev : { ...prev, course: response.data[0].course_name }));
          }
        } else if (response) {
          setCoursesError("Couldn't load courses.");
        }
      } catch (err) {
        console.error("Error fetching courses:", err);
        setCoursesError("Couldn't load courses.");
      } finally {
        setCoursesLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const validate = () => {
    const next = {};
    if (!formData.title.trim()) next.title = "Please enter a title.";
    if (!formData.course) next.course = "Please choose a course.";
    validateTiming(formData, next);
    validateExtras(formData, next);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // A new test has no sections yet, so it is saved as a draft; publish it from Edit once it has questions
  const handleSubmit = async () => {
    setError("");
    if (!validate() || submitting) return;

    setSubmitting(true);
    try {
      const response = await createTestSeriesAPI({
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        course: formData.course,
        type: formData.type,
        visibility: "draft",
        section_toggle: formData.section_toggle ? 1 : 0,
        nested_sections: formData.nested_sections ? 1 : 0,
        ...timingPayload(formData),
        ...extrasPayload(formData),
      });

      if (response?.success && formData.nested_sections && response.data && !("nested_sections" in response.data)) {
        // An API without subject support drops the flag and saves a plain test
        setError(
          "The test was saved, but as plain sections: the server does not support subjects yet. Upload the backend update and run 'php artisan migrate', then delete this test and create it again with 'Subjects with sections'."
        );
      } else if (response?.success) {
        showSuccess("Saved as a draft. Add sections and questions next, then publish it.", "Test series created");
        const newId = response.data?.id;
        navigate(newId ? `/admin/test-series/${newId}/sections` : "/admin/test-series");
      } else if (response) {
        const fieldErrors = fieldErrorsFrom(response);
        if (Object.keys(fieldErrors).length) setErrors((prev) => ({ ...prev, ...fieldErrors }));
        else setError(response.message || "Failed to create the test series. Please try again.");
      }
    } catch (err) {
      console.error("Error creating test series:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ts-form-page page">
      <div className="page-header">
        <div>
          <button type="button" className="back-link" onClick={() => navigate("/admin/test-series")}>
            <i className="bi bi-arrow-left"></i> Test series
          </button>
          <h2>Create test series</h2>
          <p>Set up the basics. It's saved as a draft; you'll add sections and questions right after.</p>
        </div>
      </div>

      <div className="ts-form-layout">
        <form
          className="card card-pad"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          noValidate
        >
          <ErrorAlert message={error} onDismiss={() => setError("")} />

          <TestSeriesForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            courses={courses}
            coursesLoading={coursesLoading}
            coursesError={coursesError}
            disabled={Boolean(submitting)}
          />

          <div className="form-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => navigate("/admin/test-series")}
              disabled={Boolean(submitting)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? <span className="btn-spinner"></span> : <i className="bi bi-arrow-right"></i>}
              Create & add sections
            </button>
          </div>
        </form>

        <aside className="ts-form-aside">
          <div className="card card-pad ts-steps">
            <h4>What happens next</h4>
            <ol>
              <li>
                <span>1</span>
                <div>
                  <strong>Add sections</strong>
                  <small>Set each section's question count, and its duration if sections are timed separately.</small>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Write questions</strong>
                  <small>Bilingual questions with math & chemistry notation.</small>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Publish</strong>
                  <small>Once every section has questions, set Visibility to Published under Edit.</small>
                </div>
              </li>
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default CreateTestSeries;
